import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Cloud, Loader2, Mail, Lock, User as UserIcon, ArrowLeft } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { useToast } from "../hooks/use-toast";
import { Toaster } from "../components/ui/toaster";
import { useAuth } from "../context/AuthContext";
import { AUTH_ERROR_KEY } from "./AuthCallback";
import ConsentCheckboxes, { LoginLegalNotice } from "../legal/ConsentCheckboxes";
import { screenMessage } from "../lib/screenMessage";
import { inAppPath } from "../lib/inAppPath";

const Login = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { signIn, signUp, googleLogin, completeCustomer2fa } = useAuth();
  const [mode, setMode] = useState("signin"); // signin | signup
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState("");
  // Signup consent: both boxes start unticked and must be ticked before email OR Google signup.
  const [consent, setConsent] = useState({ terms: false, privacy: false });
  const [pending2fa, setPending2fa] = useState("");
  const [code, setCode] = useState("");
  const consentOk = consent.terms && consent.privacy;
  const blocked = mode === "signup" && !consentOk;

  // A failed Google sign-in comes back here with the reason (set by AuthCallback).
  useEffect(() => {
    let msg = null;
    try { msg = sessionStorage.getItem(AUTH_ERROR_KEY); sessionStorage.removeItem(AUTH_ERROR_KEY); } catch {}
    if (msg) toast({ title: "Google sign-in failed", description: screenMessage("login"), variant: "destructive" });
  }, [toast]);

  const submit = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      toast({ title: "Missing details", description: "Enter your email and password." });
      return;
    }
    if (blocked) {
      toast({ title: "Please accept the terms", description: "Tick both boxes to create your account." });
      return;
    }
    if (mode === "signup" && password.length < 8) {
      toast({ title: "Password too short", description: "Use at least 8 characters." });
      return;
    }
    setBusy(true);
    try {
      if (mode === "signin") {
        if (pending2fa) {
          const done = await completeCustomer2fa(pending2fa, code.trim());
          navigate(inAppPath(done?.next));
          return;
        }
        const data = await signIn(email, password);
        if (data?.requires_2fa) {
          setPending2fa(data.pending_2fa || "");
          return;
        }
        navigate(inAppPath(data?.next));
      } else {
        await signUp(email, password, name, consent);
        setSent("Check your email to finish signing up.");
      }
    } catch {
      const screen = pending2fa ? "twofa" : mode === "signin" ? "login" : "register";
      toast({
        title: mode === "signin" ? "Sign in failed" : "Could not create account",
        description: screenMessage(screen),
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="relative flex min-h-screen items-center justify-center bg-background px-5">
      <Toaster />
      <div className="pointer-events-none absolute inset-0 bg-dots opacity-50" />
      <Link to="/" className="absolute left-6 top-6 flex items-center gap-2 text-muted-foreground hover:text-foreground">
        <ArrowLeft className="h-4 w-4" /> Back
      </Link>

      <div className="relative w-full max-w-md rounded-2xl border border-border bg-card p-8 shadow-xl">
        {sent && (
          <p className="mb-4 rounded-xl border border-border bg-secondary/40 p-3 text-sm" data-testid="signup-check-email">{sent}</p>
        )}
        <div className="flex flex-col items-center">
          <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary text-primary-foreground">
            <Cloud className="h-6 w-6" strokeWidth={2.2} />
          </span>
          <h1 className="mt-4 font-display text-2xl font-bold text-foreground">
            {pending2fa ? "Enter your authenticator code" : mode === "signin" ? "Welcome back" : "Create your account"}
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            {mode === "signin" ? "Sign in to run the Nimbus agent." : "Start automating in minutes."}
          </p>
        </div>

        {mode === "signup" && (
          <ConsentCheckboxes value={consent} onChange={setConsent} className="mt-6 text-muted-foreground" />
        )}
        {mode === "signin" && (
          <LoginLegalNotice className="mt-6 text-muted-foreground" />
        )}

        {GOOGLE_ENABLED && (
          <>
        <Button
          variant="outline"
          className="mt-6 w-full rounded-full border-border font-medium"
          disabled={blocked}
          title={blocked ? "Tick both boxes above to sign up with Google" : undefined}
          onClick={() => googleLogin(mode === "signup" ? consent : null)}
        >
          Continue with Google
        </Button>

        <div className="my-5 flex items-center gap-3">
          <span className="h-px flex-1 bg-border" />
          <span className="text-xs text-muted-foreground">or with email</span>
          <span className="h-px flex-1 bg-border" />
        </div>
          </>
        )}

        <form onSubmit={submit} className={GOOGLE_ENABLED ? "space-y-4" : "mt-6 space-y-4"}>
          {mode === "signup" && (
            <div>
              <Label htmlFor="name" className="text-sm">Name</Label>
              <div className="relative mt-1.5">
                <UserIcon className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input id="name" value={name} onChange={(e) => setName(e.target.value)}
                       placeholder="Jane Doe" className="pl-9" />
              </div>
            </div>
          )}
          <div>
            <Label htmlFor="email" className="text-sm">Email</Label>
            <div className="relative mt-1.5">
              <Mail className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                     placeholder="you@company.com" className="pl-9" required />
            </div>
          </div>
          <div>
            <Label htmlFor="password" className="text-sm">Password</Label>
            <div className="relative mt-1.5">
              <Lock className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                     placeholder={mode === "signup" ? "At least 8 characters" : "Your password"} className="pl-9" required />
            </div>
          </div>
          {pending2fa && (
            <div>
              <Label htmlFor="login-code" className="text-sm">Authenticator code</Label>
              <Input id="login-code" value={code} onChange={(e) => setCode(e.target.value)}
                     autoComplete="one-time-code" placeholder="123456" className="mt-1.5" data-testid="login-2fa-code" />
            </div>
          )}
            <Button type="submit" disabled={busy || blocked} className="w-full rounded-full bg-primary font-semibold">
            {busy ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : null}
            {mode === "signin" ? "Sign in" : "Create account"}
          </Button>
        </form>

        <p className="mt-5 text-center text-sm text-muted-foreground">
          {mode === "signin" ? "New to Nimbus?" : "Already have an account?"}{" "}
          <button
            className="font-semibold text-primary hover:underline"
            onClick={() => setMode(mode === "signin" ? "signup" : "signin")}
          >
            {mode === "signin" ? "Create an account" : "Sign in"}
          </button>
        </p>
        {mode === "signin" && (
          <div className="mt-6 border-t border-border pt-5">
            <p className="mb-2 text-[11px] uppercase tracking-[0.2em] text-muted-foreground">Operations</p>
            <Link
              to="/admin/login"
              data-testid="login-staff-admin-link"
              className="inline-flex items-center gap-1 text-sm font-medium text-foreground hover:text-primary"
            >
              Staff &amp; Admin sign-in
            </Link>
          </div>
        )}
        <p className="mt-4 text-center text-xs text-muted-foreground">
          <Link to="/terms" className="hover:underline">Terms</Link> · <Link to="/privacy" className="hover:underline">Privacy</Link> · <Link to="/cookies" className="hover:underline">Cookies</Link>
        </p>
      </div>
    </div>
  );
};

// Google sign-in uses Emergent Auth, which only works on Emergent hosting.
// Set REACT_APP_ENABLE_EMERGENT_GOOGLE=true at build time to show it; email/password always works.
const GOOGLE_ENABLED = process.env.REACT_APP_ENABLE_EMERGENT_GOOGLE === "true";

export default Login;
